import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import authService from '../services/auth'
import type { User, LoginCredentials, RegisterCredentials } from '../types/auth'
import { AxiosError } from 'axios'
import type { ApiError } from '../types/auth'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const currentUser = computed(() => user.value)

  // Actions
  async function login(credentials: LoginCredentials) {
    isLoading.value = true
    error.value = null

    try {
      const authToken = await authService.login(credentials)
      token.value = authToken

      // Fetch user data after login
      await fetchCurrentUser()

      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function register(credentials: RegisterCredentials) {
    isLoading.value = true
    error.value = null

    try {
      const authToken = await authService.register(credentials)
      token.value = authToken

      // Fetch user data after registration
      await fetchCurrentUser()

      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function confirmEmail(confirmToken: string) {
    isLoading.value = true
    error.value = null

    try {
      const authToken = await authService.confirmEmail(confirmToken)
      token.value = authToken

      // Fetch user data after confirmation
      await fetchCurrentUser()

      return true
    } catch (err) {
      handleError(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function requestPasswordReset(email: string) {
    isLoading.value = true
    error.value = null

    try {
      await authService.requestPasswordReset({ email })
      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function resetPassword(resetToken: string, password: string) {
    isLoading.value = true
    error.value = null

    try {
      const authToken = await authService.resetPassword({ token: resetToken, password })
      token.value = authToken

      await fetchCurrentUser()

      return true
    } catch (err) {
      handleError(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    authService.logout()
    user.value = null
    token.value = null
    error.value = null
  }

  async function fetchCurrentUser() {
    try {
      const userData = await authService.getCurrentUser()
      user.value = userData
    } catch (err) {
      // If fetching user fails, logout
      await logout()
      throw err
    }
  }

  async function initAuth() {
    if (isInitialized.value) return

    const storedToken = authService.getToken()

    if (storedToken) {
      token.value = storedToken

      try {
        await fetchCurrentUser()
      } catch (err) {
        // Token is invalid, clear it
        await logout()
      }
    }

    isInitialized.value = true
  }

  function clearError() {
    error.value = null
  }

  function handleError(err: unknown) {
    if (err instanceof AxiosError) {
      const apiError = err.response?.data as ApiError

      // Check for validation errors
      if (apiError?.data?.errors) {
        const errors = apiError.data.errors
        const firstErrorKey = Object.keys(errors)[0]
        error.value = errors[firstErrorKey]
      } else if (apiError?.errors) {
        const errors = apiError.errors
        const firstErrorKey = Object.keys(errors)[0]
        error.value = errors[firstErrorKey]
      } else if (err.response?.statusText) {
        error.value = err.response.statusText
      } else {
        error.value = 'An unexpected error occurred'
      }
    } else if (err instanceof Error) {
      error.value = err.message
    } else {
      error.value = 'An unexpected error occurred'
    }
  }

  return {
    // State
    user,
    token,
    isLoading,
    error,
    isInitialized,

    // Getters
    isAuthenticated,
    currentUser,

    // Actions
    login,
    register,
    confirmEmail,
    requestPasswordReset,
    resetPassword,
    logout,
    fetchCurrentUser,
    initAuth,
    clearError,
  }
})
